import type { AuthSession } from '@supabase/supabase-js'
import { Either, left, right } from 'fp-ts/lib/Either'
import { create } from 'zustand'

import { getSupabase } from '../lib/supabase'

interface SignInProps {
  email: string
  password: string
}

interface AuthState {
  session: AuthSession | null
  signInWithPassword: ({ email, password }: SignInProps) => Promise<Either<Error, AuthSession>>
  signOut: () => Promise<void>
  refreshSession: () => Promise<void>
}

export const useAuthStore = create<AuthState>(() => ({
  session: null,

  signInWithPassword: async ({ email, password }) => {
    try {
      const { data, error } = await getSupabase().auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return left(error)
      }

      useAuthStore.setState({ session: data.session })
      return right(data.session)
    } catch (error) {
      return left(error instanceof Error ? error : new Error('Unknown error during authentication'))
    }
  },

  signOut: async () => {
    try {
      const { error } = await getSupabase().auth.signOut()
      if (error) {
        console.error('Error during sign out:', error)
      }
      useAuthStore.setState({ session: null })
    } catch (error) {
      console.error('Exception during sign out:', error)
    }
  },

  refreshSession: async () => {
    try {
      const { data, error } = await getSupabase().auth.getSession()
      if (error) {
        console.error('Error refreshing session:', error)
        return
      }
      useAuthStore.setState({ session: data.session })
    } catch (error) {
      console.error('Exception refreshing session:', error)
    }
  },
}))

let listening = false

/**
 * Subscribes the store to Supabase's session once. Called by the auth provider
 * rather than on import, so nothing touches Supabase when login is skipped.
 */
export const startAuthListener = (): void => {
  if (listening) return
  listening = true
  const supabase = getSupabase()

  supabase.auth.onAuthStateChange((event, session) => {
    console.log('Auth state changed:', event)
    useAuthStore.setState({ session })
  })

  void supabase.auth
    .getSession()
    .then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting initial session:', error)
        return undefined
      }
      useAuthStore.setState({ session })
      return undefined
    })
    .catch((error: unknown) => {
      console.error('Error getting initial session:', error)
    })
}
